<template>
  <div class="jim-scheduling-container">
    <div class="trend-page">
      <h1 class="page-heading">Inventory Trend Report Data</h1>

      <div class="trend-layout">
        <div class="left-side-section">
          <button class="plan-it-btn" @click="loadReport" :disabled="loading">
            {{ loading ? "Loading…" : "Refresh" }}
          </button>

          <button class="plan-it-btn" @click="printReport" :disabled="!reportLoaded">
            Print
          </button>

          <button class="plan-it-btn" @click="exportCSV" :disabled="!reportLoaded">
            Export CSV
          </button>

          <div class="side-help">
            <div><strong>Units:</strong> Thousand lbs</div>
            <div><strong>Locations:</strong> F, D, K, L</div>
            <div><strong>Virtual WHs:</strong> WF→F, WD→D, WK→K, WL→L</div>
            <div><strong>Glass:</strong> Rolls × lbs/roll</div>
            <div><strong>Polyester:</strong> Rolls × lbs/roll</div>
            <div><strong>Wire / Adh / Yarn:</strong> QOH lbs</div>
          </div>

          <div v-if="unmapped.length" class="warning-box">
            <strong>Unmapped:</strong> {{ unmapped.length }} items
          </div>

          <div v-if="missingConversion.length" class="warning-box">
            <strong>Missing conversions:</strong> {{ missingConversion.length }} items
          </div>
        </div>

        <div class="trend-wrapper">
          <div class="summary-bar">
            <div class="summary-item">
              <div class="summary-label">Glass</div>
              <div class="summary-value">{{ formatNumber(totals.Glass) }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Wire</div>
              <div class="summary-value">{{ formatNumber(totals.Wire) }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Polyester</div>
              <div class="summary-value">{{ formatNumber(totals.Polyester) }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Adh</div>
              <div class="summary-value">{{ formatNumber(totals.Adh) }}</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">Yarn</div>
              <div class="summary-value">{{ formatNumber(totals.Yarn) }}</div>
            </div>

            <div class="summary-item total-card">
              <div class="summary-label">Total</div>
              <div class="summary-value">£{{ formatNumber(totals.Total) }}</div>
            </div>
          </div>

          <div class="table-panel">
            <div class="table-header-row">
              <div>
                <div class="small-muted" v-if="loading">Loading inventory trend…</div>
                <div class="small-muted" v-else>
                  Inventory from Macola iminvloc, converted to thousand lbs.
                  Virtual warehouses are rolled into their plant locations.
                </div>
              </div>
            </div>

            <div class="table-scroll">
              <table class="trend-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Glass</th>
                    <th>Wire</th>
                    <th>Polyester</th>
                    <th>Adh</th>
                    <th>Yarn</th>
                    <th>Total</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="row in rows" :key="row.Location">
                    <td class="location-cell">{{ row.Location }}</td>
                    <td>{{ formatNumber(row.Glass) }}</td>
                    <td>{{ formatNumber(row.Wire) }}</td>
                    <td>{{ formatNumber(row.Polyester) }}</td>
                    <td>{{ formatNumber(row.Adh) }}</td>
                    <td>{{ formatNumber(row.Yarn) }}</td>
                    <td class="total-cell">£{{ formatNumber(row.Total) }}</td>
                  </tr>

                  <tr class="new-target-row">
                    <td class="location-cell">New Target</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                  </tr>

                  <tr class="total-row">
                    <td class="location-cell">Total</td>
                    <td>{{ formatNumber(totals.Glass) }}</td>
                    <td>{{ formatNumber(totals.Wire) }}</td>
                    <td>{{ formatNumber(totals.Polyester) }}</td>
                    <td>{{ formatNumber(totals.Adh) }}</td>
                    <td>{{ formatNumber(totals.Yarn) }}</td>
                    <td class="total-cell">£{{ formatNumber(totals.Total) }}</td>
                  </tr>

                  <tr v-if="!loading && rows.length === 0">
                    <td colspan="7" class="empty-row">
                      No inventory trend data found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          

          <div v-if="missingConversion.length" class="table-panel unmapped-panel">
            <div class="table-header-row">
              <div class="small-muted">
                Missing conversion items. These matched a material group but do not have inventory_lbs_per_roll in product_details.
              </div>
            </div>

            <div class="table-scroll small-scroll">
              <table class="trend-table">
                <thead>
                  <tr>
                    <th>Item No</th>
                    <th>Source Loc</th>
                    <th>Report Loc</th>
                    <th>Group</th>
                    <th>Type</th>
                    <th>QOH</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="item in missingConversion"
                    :key="`${item.item_no}-${item.source_loc}`"
                  >
                    <td class="item-cell">{{ item.item_no }}</td>
                    <td>{{ item.source_loc }}</td>
                    <td>{{ item.report_loc }}</td>
                    <td>{{ item.material_group }}</td>
                    <td>{{ item.type }}</td>
                    <td>{{ formatNumber(item.qty_on_hand) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PabloChatSidebar :loc="selectedLocation" />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import PabloChatSidebar from "./PabloChatSidebar.vue";

export default {
  name: "InventoryTrendReport",

  components: {
    PabloChatSidebar
  },

  data() {
    return {
      loading: false,
      rows: [],
      totals: {
        Glass: 0,
        Wire: 0,
        Polyester: 0,
        Adh: 0,
        Yarn: 0,
        Total: 0
      },
      unmapped: [],
      missingConversion: []
    };
  },

  computed: {
    selectedLocation() {
      return this.$root.selectedLocation;
    },

    reportLoaded() {
      return Array.isArray(this.rows) && this.rows.length > 0;
    }
  },

  methods: {
    formatNumber(value) {
      const n = Number(value || 0);
      return n.toLocaleString(undefined, {
        maximumFractionDigits: 0
      });
    },

    async loadReport() {
      this.loading = true;

      try {
        const { data } = await axios.get("/inventory-trend-report");

        this.rows = Array.isArray(data?.rows) ? data.rows : [];

        this.totals = data?.totals || {
          Glass: 0,
          Wire: 0,
          Polyester: 0,
          Adh: 0,
          Yarn: 0,
          Total: 0
        };

        this.unmapped = Array.isArray(data?.unmapped) ? data.unmapped : [];

        this.missingConversion = Array.isArray(data?.missingConversion)
          ? data.missingConversion
          : [];
      } catch (err) {
        console.error("load inventory trend report error:", err);
        alert("Failed to load inventory trend report.");
      } finally {
        this.loading = false;
      }
    },

    printReport() {
      if (!this.reportLoaded) return;

      const payload = {
        meta: {
          generatedAt: new Date().toISOString(),
          units: "Thousand lbs",
          source: "Macola iminvloc",
          virtualWarehouses: "WF→F, WD→D, WK→K, WL→L"
        },
        rows: this.rows,
        totals: this.totals
      };

      sessionStorage.setItem(
        "pablo_inventory_trend_print_payload",
        JSON.stringify(payload)
      );

      const routeData = this.$router.resolve({
        path: "/print-inventory-trend-report"
      });

      window.open(routeData.href, "_blank");
    },

    exportCSV() {
      if (!this.reportLoaded) return;

      const csvRows = [];

      csvRows.push([
        "Location",
        "Glass",
        "Wire",
        "Polyester",
        "Adh",
        "Yarn",
        "Total"
      ]);

      for (const row of this.rows) {
        csvRows.push([
          row.Location,
          Number(row.Glass || 0),
          Number(row.Wire || 0),
          Number(row.Polyester || 0),
          Number(row.Adh || 0),
          Number(row.Yarn || 0),
          Number(row.Total || 0)
        ]);
      }

      csvRows.push([
        "New Target",
        "",
        "",
        "",
        "",
        "",
        ""
      ]);

      csvRows.push([
        "Total",
        Number(this.totals.Glass || 0),
        Number(this.totals.Wire || 0),
        Number(this.totals.Polyester || 0),
        Number(this.totals.Adh || 0),
        Number(this.totals.Yarn || 0),
        Number(this.totals.Total || 0)
      ]);

      const csv = csvRows
        .map((r) =>
          r
            .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;"
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const today = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.setAttribute("download", `inventory_trend_${today}.csv`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  },

  mounted() {
    this.loadReport();
  }
};
</script>

<style scoped>
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  margin-top: 20px;
  background-color: #0b0101;
  min-height: 100vh;
  padding-bottom: 40px;
  text-align: left !important;
}

.page-heading {
  font-family: "Cormorant Garamond", serif;
  font-size: 36px;
  color: #cbbfbf;
  margin-bottom: 20px;
  text-align: center;
}

.trend-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.trend-layout {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
  color: #fff;
  margin-top: 80px;
}

.trend-wrapper {
  width: 100%;
  min-width: 0;
}

.btn,
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 125px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}

.btn:hover:not(:disabled),
.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.btn:disabled,
.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background: rgb(67, 67, 67);
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 120px;
  flex: 1 1 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.8;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #cbbfbf;
  line-height: 1.1;
  margin-top: 2px;
}

.total-card .summary-value {
  color: #f5d76e;
}

.table-panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
  margin-bottom: 12px;
}

.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
}

.table-scroll {
  max-height: 78vh;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #333;
}

.small-scroll {
  max-height: 320px;
}

.trend-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.trend-table th,
.trend-table td {
  border: 1px solid #333;
  padding: 8px 10px;
  font-size: 15px;
  text-align: right;
  white-space: nowrap;
}

.trend-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
}

.trend-table th:first-child,
.trend-table td:first-child {
  text-align: left;
}

.trend-table tr:nth-child(even) td {
  background: #0f141c;
}

.trend-table tr:nth-child(odd) td {
  background: #0b0101;
}

.location-cell,
.item-cell {
  font-weight: bold;
  color: #e5e7eb;
}

.total-cell {
  font-weight: bold;
  color: #f5d76e;
}

.total-row td {
  background: #1f2937 !important;
  font-weight: bold;
  border-top: 2px solid #888;
}

.new-target-row td {
  background: #15100a !important;
  color: #ffd166;
  font-style: italic;
}

.empty-row {
  text-align: center !important;
  padding: 18px !important;
  opacity: 0.75;
}

.small-muted {
  font-size: 12px;
  opacity: 0.75;
  color: #cbbfbf;
}

.side-help,
.warning-box {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px;
  background: rgb(67, 67, 67);
  font-size: 13px;
  line-height: 1.6;
  color: #cbbfbf;
}

.warning-box {
  background: #3a2d09;
  color: #ffd166;
  border-color: #9a7b16;
}

.unmapped-panel {
  margin-top: 14px;
}
</style>